// AccordionFAQComponent.js
import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, Collapse, Button } from '@mui/material';
import gsap from 'gsap';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Image from '../../Image/Image';

const ARROW_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Tailless-Line-Arrow-Right-Circle.svg';

const AccordionFAQComponent = ({ data, defaultOpenIndex = null }) => {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex); // 4th open on load
  const buttonRef = useRef(null);

  // Bounce animation that resolves when complete
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleButtonClick = async (link) => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRef.current);
    }
    if (link) window.open(link, '_blank');
  };

  const toggle = (index) => setOpenIndex(index === openIndex ? null : index);

  const CELL_TEXT_SX = {
    color: '#162F3B',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    letterSpacing: '-0.002em',
    m: 0,
    width: '100%',
    textAlign: 'center',
  };

  const IconCell = ({ icon }) => (
    <Typography
      component="div"
      role="img"
      aria-label={icon === 'check' ? 'Yes' : 'No'}
      sx={CELL_TEXT_SX}
    >
      {icon === 'check' ? '✓' : '✕'}
    </Typography>
  );

  const PlanTable = ({ table }) => (
    <Box
      sx={{
        mt: '16px',
        borderRadius: '24px',
        overflow: 'hidden',
        border: '1px solid #E3E6E8',
        bgcolor: '#FFF',
      }}
    >
      {table.rows.map((r, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            alignItems: 'stretch',
            borderBottom:
              i === table.rows.length - 1 ? 'none' : '1px solid #E3E6E8',
            minHeight: 56,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '16px',
              py: '12px',
              height: '100%',
            }}
          >
            <Typography sx={CELL_TEXT_SX}>{r.label}</Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '16px',
              py: '12px',
              height: '100%',
              gap: '8px',
            }}
          >
            {typeof r.value === 'string' ? (
              <Typography sx={CELL_TEXT_SX}>{r.value}</Typography>
            ) : (
              <IconCell icon={r.value.icon} />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        width: '1050px',
        mx: 'auto',

        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: '24px',
      }}
    >
      {/* ✅ Left Title Container */}
      <Box
        sx={{
          width: '400px', // fixed column for the title
          flex: '0 0 326px',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
      >
        <ScrollFadeWrapper variant="text">
          <Typography
            component="h2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '3rem',
              lineHeight: '3.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mt: '40px',
              textAlign: 'center',
              whiteSpace: 'pre-line',
            }}
          >
            {data.title}
          </Typography>
        </ScrollFadeWrapper>
      </Box>

      {/* ✅ Accordion Container */}
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          minWidth: 0,
        }}
      >
        {/* Blue Container */}
        <Box
          sx={{
            width: '620px',
            backgroundColor: '#162F3B',
            borderRadius: '64px',
            pt: '40px',
            pb: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxSizing: 'border-box',
          }}
        >
          {data.introText && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mb: '20px',
              }}
            >
              <Box
                sx={{
                  width: '1010px',
                  backgroundColor: '#162F3B',
                  borderRadius: '24px',
                  p: '40px',
                  boxSizing: 'border-box',
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '1.5rem',
                    lineHeight: '1.75rem',
                    letterSpacing: '-0.03em',
                    textAlign: 'center',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {data.introText}
                </Typography>
              </Box>
            </Box>
          )}

          {data.faq.map((faq, i) => {
            const isOpen = openIndex === i;

            return (
              <ScrollFadeWrapper key={i}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => toggle(i)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(i);
                    }
                  }}
                  sx={{
                    width: '700px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '24px',
                    boxShadow:
                      '0px 3px 8px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB133',
                    p: '16px',
                    mt: i === 0 ? '20px' : 0,
                    mb: i === data.faq.length - 1 ? '20px' : '24px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    transition: 'box-shadow 0.2s ease',
                    outline: 'none',
                    '&:focus-visible': { boxShadow: '0 0 0 3px #A2ACB166' },
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                      }}
                    >
                      {faq.title}
                    </Typography>

                    <IconButton
                      disableRipple
                      tabIndex={-1}
                      aria-hidden="true"
                      sx={{
                        width: '32px',
                        height: '32px',
                        color: '#162F3B',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease',
                      }}
                    >
                      <ArrowDropDownIcon sx={{ fontSize: '32px' }} />
                    </IconButton>
                  </Box>

                  {/* Animated content */}
                  <Collapse in={isOpen} timeout={250}>
                    {faq.table ? (
                      <PlanTable table={faq.table} />
                    ) : (
                      <Typography
                        sx={{
                          mt: '16px',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          letterSpacing: '-0.02em',
                          color: '#162F3B',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {faq.description}
                      </Typography>
                    )}
                  </Collapse>
                </Box>
              </ScrollFadeWrapper>
            );
          })}
        </Box>

        {/* Button */}
        {data.footer && (
          <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center' }}>
            <Button
              ref={buttonRef}
              variant="contained"
              disableElevation
              endIcon={
                <Image
                  width={24}
                  height={24}
                  src={ARROW_ICON_URL}
                  alt="Arrow icon"
                  sx={{ width: 24, height: 24 }}
                />
              }
              onClick={() => handleButtonClick(data.footerLink)}
              sx={{
                mt: '-40px',
                minWidth: '231px',
                minHeight: '48px',
                gap: '4px',
                borderRadius: '24px',
                padding: '12px 24px',
                backgroundColor: '#A2ACB133',
                textTransform: 'none',
                willChange: 'transform',
                '&:hover': { backgroundColor: '#393c3e33' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.03em',
                  color: '#162F3B',
                }}
              >
                {data.footer}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccordionFAQComponent;
